/** Permission keys that grant access to the Settings page. */
import { permissionKey } from "@/lib/rbacCatalog";

export const SETTINGS_GATE_PERMISSIONS = [
  permissionKey("settings.sap-landscape", "read"),
  permissionKey("settings.mcp-servers", "read"),
  permissionKey("settings.general", "read"),
  permissionKey("settings.user-management", "read"),
  permissionKey("settings.group-management", "read"),
];

/** Settings tab ids — must match TabsTrigger value in Settings.js */

export const SETTINGS_TABS = {
  SAP_LANDSCAPE: "sap-landscape",
  MCP_SERVERS: "mcp-servers",
  GENERAL: "general",
  AGENTS: "agents",
  INTEGRATIONS: "integrations",
  NOTIFICATIONS: "notifications",
  SECURITY: "security",
  PERFORMANCE: "performance",
  USER_MANAGEMENT: "user-management",
  GROUP_MANAGEMENT: "group-management",
};

/** Shared gate for General + unused placeholder tabs (Agents, Integrations, …). */
const GENERAL_TAB_PERMISSIONS = [permissionKey("settings.general", "read")];

/** Permission keys required to see each tab (any one). */
export const SETTINGS_TAB_PERMISSIONS = {
  [SETTINGS_TABS.GENERAL]: GENERAL_TAB_PERMISSIONS,
  [SETTINGS_TABS.NOTIFICATIONS]: GENERAL_TAB_PERMISSIONS,
  [SETTINGS_TABS.SAP_LANDSCAPE]: [permissionKey("settings.sap-landscape", "read")],
  [SETTINGS_TABS.MCP_SERVERS]: [permissionKey("settings.mcp-servers", "read")],
  [SETTINGS_TABS.USER_MANAGEMENT]: [permissionKey("settings.user-management", "read")],
  [SETTINGS_TABS.GROUP_MANAGEMENT]: [permissionKey("settings.group-management", "read")],
  [SETTINGS_TABS.AGENTS]: GENERAL_TAB_PERMISSIONS,
  [SETTINGS_TABS.INTEGRATIONS]: GENERAL_TAB_PERMISSIONS,
  [SETTINGS_TABS.SECURITY]: GENERAL_TAB_PERMISSIONS,
  [SETTINGS_TABS.PERFORMANCE]: GENERAL_TAB_PERMISSIONS,
};

const TAB_ORDER = [
  SETTINGS_TABS.SAP_LANDSCAPE,
  SETTINGS_TABS.MCP_SERVERS,
  SETTINGS_TABS.USER_MANAGEMENT,
  SETTINGS_TABS.GROUP_MANAGEMENT,
  SETTINGS_TABS.GENERAL,
  SETTINGS_TABS.NOTIFICATIONS,
  SETTINGS_TABS.AGENTS,
  SETTINGS_TABS.INTEGRATIONS,
  SETTINGS_TABS.SECURITY,
  SETTINGS_TABS.PERFORMANCE,
];

/**
 * @param {string} tab
 * @param {{ hasPermissionKey: (key: string) => boolean }} access
 */
export function canViewSettingsTab(tab, access) {
  const keys = SETTINGS_TAB_PERMISSIONS[tab];
  if (!keys?.length) return false;
  return keys.some((key) => access.hasPermissionKey(key));
}

/**
 * @param {{ hasPermissionKey: (key: string) => boolean }} access
 * @returns {string}
 */
export function defaultSettingsTab(access) {
  for (const tab of TAB_ORDER) {
    if (canViewSettingsTab(tab, access)) return tab;
  }
  return SETTINGS_TABS.GENERAL;
}

/**
 * @param {string} tab
 * @param {{ hasPermissionKey: (key: string) => boolean }} access
 * @returns {string}
 */
export function coerceSettingsTab(tab, access) {
  if (canViewSettingsTab(tab, access)) return tab;
  return defaultSettingsTab(access);
}
